import { supabase } from '@/integrations/supabase/client';
import { analyticsEvents, track } from '@/lib/analytics/posthog';
import {
  clearSidewalkUpvotes, getDeviceToken, pendingSidewalkUpvotes, rememberSidewalkUpvote,
} from '@/lib/sidewalk/device';

export async function addSidewalkUpvote(displayId: number): Promise<{ added: boolean; count: number }> {
  const deviceToken = getDeviceToken();
  if (!deviceToken) throw new Error('Upvotes need browser storage. Sign in to upvote instead.');

  const response = await fetch(`/api/displays/${displayId}/sidewalk-upvote`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ deviceToken }),
  });
  const body = (await response.json().catch(() => ({}))) as { added?: unknown; count?: unknown; error?: unknown };
  if (!response.ok) throw new Error(typeof body.error === 'string' ? body.error : 'Could not upvote');

  rememberSidewalkUpvote(displayId);
  if (body.added === true) track(analyticsEvents.SIGN_UPVOTED, { display_id: String(displayId) });
  return { added: body.added === true, count: typeof body.count === 'number' ? body.count : 0 };
}

/** Moves this device's sidewalk upvotes into the signed-in account. Returns how many moved. */
export async function mergeSidewalkUpvotes(): Promise<number> {
  if (pendingSidewalkUpvotes().length === 0) return 0;
  const deviceToken = getDeviceToken();
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!deviceToken || !token) return 0;

  const response = await fetch('/api/sidewalk-upvotes/merge', {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ deviceToken }),
  });
  if (!response.ok) return 0;
  const body = (await response.json().catch(() => ({}))) as { moved?: unknown };
  const moved = typeof body.moved === 'number' ? body.moved : 0;
  clearSidewalkUpvotes();
  if (moved > 0) track(analyticsEvents.SIGN_UPVOTES_SAVED, { moved });
  return moved;
}
